import React, { useContext, useMemo, useState, useEffect } from 'react';
import { InputText, ReactSelect } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { GlobalListType } from '@/generated/data.schemas';
import { useGetGlobalList } from '@/generated/global-lists';
import { useGetTranslations } from '@/generated/translations';
import styles from '../ApplicationDetail.module.scss';
import type { Option } from './types';

const OtherOption = {
	label: 'Other',
	value: 'Other',
};

interface GlobalListDropdownProps {
	id: string;
	type: GlobalListType;
	initialValue?: string | null;
	handleChange: () => void;
	segment?: string;
}

export const GlobalListDropdown: React.FunctionComponent<GlobalListDropdownProps> = ({
	id,
	type,
	initialValue,
	handleChange,
	segment,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const [value, setValue] = useState(initialValue || undefined);
	const [showOtherInput, setShowOtherInput] = useState(initialValue && initialValue.toLowerCase() === 'other');

	const { data: list, isLoading } = useGetGlobalList(segmentationId, type, {
		SegmentId: segment,
	});

	const options = useMemo(() => {
		const options =
			list?.data.map((x) => {
				return {
					label: x,
					value: x,
				};
			}) || [];
		options.push(OtherOption);
		return options;
	}, [list]);

	const selectedValue = useMemo(() => {
		return value ? (options?.find((x) => x.value === value) ?? OtherOption) : undefined;
	}, [value, options]);

	const modifyValue = (e) => {
		setValue(e.target.value);
		handleChange();
	};

	const onDropdownChange = (option: Option) => {
		setValue(option.value.toLowerCase() === 'other' ? '' : option.value);
		setShowOtherInput(option.value.toLowerCase() === 'other');
		handleChange();
	};

	useEffect(() => {
		if (list && initialValue) {
			setShowOtherInput(list?.data.indexOf(initialValue) === -1);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [list]);

	return (
		<fieldset className={styles.fieldset}>
			<legend className={styles.legend}>{translations?.data.applications[id]}</legend>
			<div className={styles.flex}>
				{!isLoading && (
					<ReactSelect
						id={id}
						ariaLabel={translations?.data.applications[id] || ''}
						options={options}
						currentValue={selectedValue}
						onChange={onDropdownChange}
						variant={'inverse'}
						hideLabel
						isSmall
					/>
				)}
				<InputText
					name={id}
					id={id}
					value={value || ''}
					label={translations?.data.applications[id]}
					onChange={modifyValue}
					variant={'inverse'}
					isHidden={!showOtherInput}
					className={styles.linkedInput}
					inputClassName={styles.input}
					hideLabel
				/>
			</div>
		</fieldset>
	);
};
